import React from 'react';
import { useT } from '../../../i18n/LanguageContext';
import { money } from '../formatters';

/**
 * The open documents behind one customer's total.
 *
 * Rendered from the data ALREADY loaded, not a second request. Checking a total meant downloading a
 * PDF per customer against a route throttled to one request a minute, which is not a way to verify
 * six accounts, let alone sixty - and the preview response has carried these lines all along.
 */
export default function LineItems({ statement }) {
  const t = useT();
  const lines = statement?.lines || [];
  if (lines.length === 0) {
    return (
      <p className="text-[13px] text-[#64748b] pt-1">{t('No open documents on this account.')}</p>
    );
  }
  return (
    <div className="bg-white rounded-lg border border-[#e2e8f0] overflow-x-auto">
      <table className="w-full text-[13px]">
        <thead>
          <tr className="text-left text-[11px] uppercase tracking-wide text-[#94a3b8] border-b border-[#f1f5f9]">
            <th className="px-3 py-2 font-medium">{t('Date')}</th>
            <th className="px-3 py-2 font-medium">{t('Type')}</th>
            <th className="px-3 py-2 font-medium">{t('Document')}</th>
            <th className="px-3 py-2 font-medium">{t('Due')}</th>
            <th className="px-3 py-2 font-medium text-right">{t('Days past due')}</th>
            <th className="px-3 py-2 font-medium text-right">{t('Amount')}</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((l, i) => (
            <tr
              key={`${l.docNumber || 'doc'}-${i}`}
              className="border-b border-[#f8fafc] last:border-0"
            >
              <td className="px-3 py-2 text-[#475569]">{l.date || '--'}</td>
              <td className="px-3 py-2 text-[#475569] capitalize">{l.type || '--'}</td>
              <td className="px-3 py-2 text-[#0f172a]">{l.docNumber || '--'}</td>
              <td className="px-3 py-2 text-[#475569]">{l.dueDate || '--'}</td>
              <td className="px-3 py-2 text-right tabular-nums">
                {typeof l.daysPastDue === 'number' && l.daysPastDue > 0 ? (
                  <span className="text-[#f23e41]">{l.daysPastDue}</span>
                ) : (
                  <span className="text-[#94a3b8]">--</span>
                )}
              </td>
              <td
                className={`px-3 py-2 text-right tabular-nums ${
                  l.openAmount < 0 ? 'text-[#0089ff]' : 'text-[#0f172a] font-medium'
                }`}
              >
                {money(l.openAmount, statement.currency)}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="border-t border-[#e2e8f0]">
            <td colSpan={5} className="px-3 py-2 text-right text-[#64748b]">
              {t('Total due')}
            </td>
            <td className="px-3 py-2 text-right font-semibold tabular-nums">
              {money(statement.totalOpen, statement.currency)}
            </td>
          </tr>
        </tfoot>
      </table>
      {statement.linesTruncated && (
        <p className="text-[12px] text-[#005fb8] px-3 py-2 border-t border-[#f1f5f9]">
          {t(
            'This account has {total} open documents; only the first {shown} are shown and printed.',
            { total: statement.totalLineCount, shown: lines.length }
          )}
        </p>
      )}
    </div>
  );
}
